import { useQuery } from '@tanstack/react-query';
import {
  StatusCode,
  type ExperimentsResponse,
  getExperiments,
  type GetExperimentsParams,
} from '@hungryroot/rest';

const DEFAULT_PARAMS: GetExperimentsParams = {
  limit: 1000,
  offset: 0,
};

type QueryOptions = {
  enabled?: boolean;
};

// Allows consumers interested in the error to catch a specific error instead of
// also getting generic errors such as fetching errors due to networking.
export class ExperimentDefinitionsApiError extends Error {
  constructor(
    public readonly data: ExperimentsResponse,
    public readonly status: number
  ) {
    super('Experiment definitions API error');
    this.name = 'ExperimentDefinitionsApiError';
  }
}

export async function fetchExperimentDefinitions(
  params: GetExperimentsParams = {}
) {
  const res = await getExperiments({
    ...DEFAULT_PARAMS,
    ...params,
  });
  const { status } = res;
  if (
    status < StatusCode.SuccessOK ||
    status > StatusCode.RedirectMultipleChoices
  ) {
    throw new ExperimentDefinitionsApiError(res.data, status);
  }

  return res.data;
}

export function experimentDefinitionsKey(params: GetExperimentsParams = {}) {
  return ['experiment-definitions', params];
}

export function useExperimentDefinitions(
  params: GetExperimentsParams = {},
  options: QueryOptions = {}
) {
  return useQuery({
    queryKey: experimentDefinitionsKey({
      ...DEFAULT_PARAMS,
      ...params,
    }),
    queryFn: async () => {
      return await fetchExperimentDefinitions(params);
    },
    ...options,
    staleTime: 5 * 60 * 1000,
  });
}
